/**
 * Locale-aware URL for the strap/bracelet sizing-tool PDF.
 *
 * Prefix is chosen from a brand × environment map, then a language code is
 * appended (`EN.pdf`, `FR.pdf`, …).
 */

import { BRAND, type Brand } from '@/lib/cms/config';
import { getLanguageFromLocale } from '@/lib/i18n/config';

type SizingToolEnv = 'tst' | 'int' | 'pre' | 'prd';

/** CDN host + path prefix (language code + `.pdf` is appended). */
const SIZING_TOOL_URL: Record<Brand, Partial<Record<SizingToolEnv, string>>> = {
  gallet: {
    tst: 'https://www-assets-tst.breitling.com/manuals/Gallet_Sizing_Tool_Bracelet/Gallet_Sizing_Tool_Bracelet_',
    int: 'https://www-assets-int.breitling.com/manuals/Gallet_Sizing_Tool_Bracelet/Gallet_Sizing_Tool_Bracelet_',
    pre: 'https://www-assets-pre.breitling.com/manuals/Gallet_Sizing_Tool_Bracelet/Gallet_Sizing_Tool_Bracelet_',
    // Prod host has no env segment (same pattern as other brand assets).
    prd: 'https://www-assets.breitling.com/manuals/Gallet_Sizing_Tool_Bracelet/Gallet_Sizing_Tool_Bracelet_',
  },
  breitling: {
    tst: 'https://www-assets-tst.breitling.com/manuals/Breitling_Sizing_Tool_Bracelet/Breitling_Sizing_Tool_Bracelet_',
    int: 'https://www-assets-int.breitling.com/manuals/Breitling_Sizing_Tool_Bracelet/Breitling_Sizing_Tool_Bracelet_',
    pre: 'https://www-assets-pre.breitling.com/manuals/Breitling_Sizing_Tool_Bracelet/Breitling_Sizing_Tool_Bracelet_',
    prd: 'https://www-assets.breitling.com/manuals/Breitling_Sizing_Tool_Bracelet/Breitling_Sizing_Tool_Bracelet_',
  },
  ug: {},
};

const SIZING_TOOL_LANGUAGE_CODES = [
  'en',
  'es',
  'fr',
  'zh',
  'de',
  'it',
  'ja',
  'ko',
  'pt',
  'nl',
] as const;

type SizingToolLanguage = (typeof SIZING_TOOL_LANGUAGE_CODES)[number];

function isSizingToolLanguage(value: string): value is SizingToolLanguage {
  return (SIZING_TOOL_LANGUAGE_CODES as readonly string[]).includes(value);
}

/**
 * Map deploy env labels onto the assets CDN segment.
 * NEXT_PUBLIC_ALGOLIA_ENVIRONMENT is inlined into client bundles via next.config.ts
 * (SizesModal calls getSizingToolHref in the browser). ALGOLIA_ENVIRONMENT is
 * server-only and used as fallback for server-side callers.
 */
function resolveSizingToolEnv(): SizingToolEnv {
  const raw = (
    process.env.NEXT_PUBLIC_ALGOLIA_ENVIRONMENT ||
    process.env.ALGOLIA_ENVIRONMENT ||
    'tst'
  ).toLowerCase();

  if (raw === 'int' || raw === 'integration') return 'int';
  if (raw === 'pre' || raw === 'pre-production' || raw === 'preprod') return 'pre';
  if (raw === 'prd' || raw === 'prod' || raw === 'production') return 'prd';
  return 'tst';
}

function getSizingToolPrefix(brand: Brand): string | null {
  const env = resolveSizingToolEnv();
  return SIZING_TOOL_URL[brand][env] ?? SIZING_TOOL_URL[brand].tst ?? null;
}

/**
 * Resolve the sizing-tool PDF URL for a storefront locale (e.g. `us-en`).
 * Returns `null` when the current brand has no PDF configured.
 *
 * Example Input:
 * locale="fr-fr"
 *
 * Example Output:
 * "https://www-assets-tst.breitling.com/manuals/Gallet_Sizing_Tool_Bracelet/Gallet_Sizing_Tool_Bracelet_FR.pdf"
 */
export function getSizingToolHref(locale: string, brand: Brand = BRAND): string | null {
  const prefix = getSizingToolPrefix(brand);
  if (!prefix) return null;

  // `zht` has no dedicated PDF — use Simplified Chinese ZH.
  const language = getLanguageFromLocale(locale);
  const pdfLanguage = language === 'zht' ? 'zh' : language;
  const code = isSizingToolLanguage(pdfLanguage) ? pdfLanguage.toUpperCase() : 'EN';

  return `${prefix}${code}.pdf`;
}
